import { Card, useDialog } from "@fpsoftware/mvp";
import { TbEdit } from "react-icons/tb";
import { useParams } from "react-router-dom";
import {
  DateFormat,
  formatAmount,
  formatDate,
  formatMoney,
  formatMonth,
  formatPercent,
} from "shared";
import { AddressCard } from "@/addresses/AddressCard";
import { BorrowerCard } from "@/borrower/BorrowerCard";
import { Button } from "@/components/Button";
import { EditableValue } from "@/components/EditableValue/EditableValue";
import { Container } from "@/components/grid/Container";
import { Grid } from "@/components/grid/Grid";
import { AppPage } from "@/layout/AppLayout";
import { DialogTaxOptions } from "@/mortgages/DialogTaxOptions";
import { EditMortgageDetails } from "@/mortgages/EditMortgageDetails";
import { EditMortgageNote } from "@/mortgages/EditMortgageNote";
import { EditMortgageTimeline } from "@/mortgages/EditMortgageTimeline";
import { useMortgage } from "@/mortgages/hooks";
import { MortgageDebtsCard } from "@/mortgages/MortgageDebtsCard";
import { MortgageEscrowBreakdownCard } from "@/mortgages/MortgageEscrowBreakdownCard";
import { OfferList } from "@/mortgages/OfferList";
import { UploadsCard } from "@/uploads/UploadsCard";

export const PageMortgageDetail = () => {
  const { id } = useParams();
  const mortgageID = Number(id);
  const mortgage = useMortgage(mortgageID);
  const { open } = useDialog();

  const handleEditTimeline = () =>
    open(EditMortgageTimeline, {
      mortgageID: mortgage.ID,
    });

  const handleEditNote = () =>
    open(EditMortgageNote, { mortgageID: mortgage.ID });

  function handleMortgageDetailsEdit() {
    open(EditMortgageDetails, { mortgageID: mortgage.ID });
  }

  function handleTaxOptionsEdit() {
    open(DialogTaxOptions, { mortgageID: mortgage.ID });
  }

  return (
    <AppPage title="Mortgage detail">
      <Container>
        <Grid className="mb-3">
          <Grid.Col xs={6}>
            <Card className="mb-3">
              <Card.Header>
                Current mortgage details
                <Button
                  icon={TbEdit}
                  size="sm"
                  onClick={handleMortgageDetailsEdit}
                >
                  Edit details
                </Button>
              </Card.Header>
              <Card.List>
                <Card.ListItem label="Mortgage balance">
                  {formatMoney(mortgage.data.balance)}
                </Card.ListItem>
                <Card.ListItem label="Interest rate">
                  {formatPercent(mortgage.data.interestRate)}
                </Card.ListItem>
                <Card.ListItem label="Property tax prepaid amount">
                  <EditableValue
                    label={formatAmount(
                      mortgage.remortgage.prepays.propertyTax.months,
                      0,
                    )}
                    onEdit={handleTaxOptionsEdit}
                  />
                </Card.ListItem>
                <Card.ListItem label="Escrow balance">
                  {formatMoney(mortgage.data.escrowBalance)}
                </Card.ListItem>
                <Card.ListItem label="Property insurance effective date">
                  {formatDate(mortgage.data.insuranceEffectiveDate)}
                </Card.ListItem>
                <Card.ListItem label="Principal payment">
                  {formatMoney(mortgage.data.payments.principal)}/mo
                </Card.ListItem>
                <Card.ListItem label="Interest payment">
                  {formatMoney(mortgage.data.payments.interest)}/mo
                </Card.ListItem>
                <Card.ListItem label="Escrow payment">
                  {formatMoney(mortgage.data.payments.escrow)}/mo
                </Card.ListItem>
              </Card.List>
            </Card>
            <MortgageDebtsCard mortgageID={mortgage.ID} />
          </Grid.Col>
          <Grid.Col xs={6}>
            <Card>
              <Card.Header>
                Payoff
                <Button icon={TbEdit} size="sm" onClick={handleEditTimeline}>
                  Edit timeline
                </Button>
              </Card.Header>
              <Card.List>
                <Card.ListItem label="Last payment date">
                  {mortgage.data.payoff.lastPaymentDate.format(DateFormat)}
                </Card.ListItem>
                <Card.ListItem label="Est. closing date">
                  {mortgage.data.payoff.estClosingDate.format(DateFormat)}
                </Card.ListItem>
                <Card.ListItem label="Funding date">
                  {mortgage.data.payoff.fundingDate.format(DateFormat)}
                </Card.ListItem>
                <Card.ListItem label="First payment date">
                  {formatDate(mortgage.data.payoff.firstPaymentDate)}
                </Card.ListItem>
                <Card.ListItem label="# of days (plus a 6 day cushion)">
                  {mortgage.data.payoff.interestDays}
                </Card.ListItem>
                <Card.ListItem label="Interest Due">
                  {formatMoney(mortgage.data.payoff.interestDue)}
                </Card.ListItem>
                <Card.ListItem label="Skipped payments">
                  {formatMoney(mortgage.data.skippedPayments.amount)}
                  <br />
                  <small>
                    {mortgage.data.skippedPayments.months
                      .map((month) => formatMonth(month))
                      .join(", ")}
                  </small>
                </Card.ListItem>
              </Card.List>
            </Card>
          </Grid.Col>
          <Grid.Col xs={6}>
            <AddressCard addressID={mortgage.addressID} className="mb-3" />
            <UploadsCard uploadIDs={mortgage.uploadIDs} className="mb-3" />
            <Card>
              <Card.Header>
                Note
                <Button icon={TbEdit} size="sm" onClick={handleEditNote}>
                  Edit note
                </Button>
              </Card.Header>
              <Card.Body>{mortgage.note || <i>No note provided.</i>}</Card.Body>
            </Card>
          </Grid.Col>

          <Grid.Col xs={6}>
            <MortgageEscrowBreakdownCard
              mortgageID={mortgage.ID}
              className="mb-3"
            />
            <BorrowerCard borrowerID={mortgage.clientID} />
          </Grid.Col>
        </Grid>
        <hr />
        <OfferList mortgageID={mortgage.ID} />
      </Container>
    </AppPage>
  );
};
